import { Chip } from '@/components';
import CopyButton from '@/components/molecules/CopyButton/CopyButton';
import { TableRowProps } from '@/components/molecules/Table/interfaces/Table';
import Table from '@/components/molecules/Table/Table';
import { useWalletsContext } from '@/components/providers/postauth/UserWalletsProvider';
import { TransactionInterface } from '@/interfaces';
import { getColorBasedOnState } from '@/utils/colorUtils';
import { formatToSentenceCase } from '@/utils/formatUtils';
import {
  getTransactionTypeDetails,
  getWalletAddressFromTransaction,
} from '@/utils/functionalUtils';
import { FC, useMemo } from 'react';
import AmountListItem from '../AmountListItem/AmountListItem';
import TokenListItem from '../TokenListItem/TokenListItem';
import UnderlyingYieldBearingAssetListItem from '../UnderlyingYieldBearingAssetListItem/UnderlyingYieldBearingAssetListItem';
import YieldBearingAssetListItem from '../YieldBearingAssetListItem/YieldBearingAssetListItem';
interface TransactionDetailListProps {
  transaction: TransactionInterface;
}
const TransactionDetailList: FC<TransactionDetailListProps> = ({
  transaction,
}) => {
  const { wallets } = useWalletsContext();
  const walletAddresses = useMemo(
    () => wallets.map((wallet) => wallet.address),
    [wallets],
  );
  const { mainText, subText } = getTransactionTypeDetails({
    transaction,
    walletAddresses,
  });
  const columnWidths = [30, 70];
  const commonRows: TableRowProps[] = [
    {
      cells: [
        { mainText: 'Type' },
        {
          mainText:
            transaction.type === 'trade'
              ? 'Trade'
              : transaction.type === 'yield-bearing-asset-deposit'
              ? 'Earn'
              : mainText,
          subText:
            (transaction.type === 'wallet-deposit' ||
              transaction.type === 'wallet-withdrawal') &&
            subText ? (
              <CopyButton
                value={getWalletAddressFromTransaction({
                  transaction,
                  walletAddresses,
                })}
                showLabel={false}
                labelCopied="Copied"
                labelCopy="Copy"
                displayValue={subText}
                iconSize="sm"
                displayValueSize="sm"
                displayValueColor="secondary"
              />
            ) : undefined,
          align: 'right' as 'right',
        },
      ],
    },
  ];
  if (transaction.type === 'yield-bearing-asset-deposit') {
    const earnRows: TableRowProps[] = [
      {
        cells: [
          { mainText: 'Strategy' },
          {
            mainText: transaction.yieldBearingAsset ? (
              <YieldBearingAssetListItem
                yieldBearingAsset={transaction.yieldBearingAsset}
                align="right"
              />
            ) : (
              'N/A'
            ),
            align: 'right' as 'right',
          },
        ],
      },
      {
        cells: [
          { mainText: 'Bought' },
          {
            mainText: (
              <div className="grid gap-sm-wholestep-dec">
                {transaction.yieldBearingAsset?.underlyingYieldBearingAssets.map(
                  (asset) => (
                    <UnderlyingYieldBearingAssetListItem
                      key={asset.id}
                      underlyingYieldBearingAsset={asset}
                      subText={`${asset.symbol} (${(
                        asset.composition * 100
                      ).toFixed(2)}%)`}
                      align="right"
                    />
                  ),
                ) || 'N/A'}
              </div>
            ),
            align: 'right' as 'right',
          },
        ],
      },
      {
        cells: [
          { mainText: 'Sold' },
          {
            mainText: transaction.asset ? (
              <TokenListItem
                token={transaction.asset}
                align="right"
                showTicker={false}
                showNetwork
                showNetworkPrefix
                showSubNetworkNextToMainNetwork
              />
            ) : (
              'N/A'
            ),
            align: 'right' as 'right',
          },
        ],
      },
      {
        cells: [
          { mainText: 'Amount' },
          {
            mainText: (
              <AmountListItem
                amount={parseFloat(transaction.amount)}
                subAmount={parseFloat(transaction.amountInUsd)}
                symbol={transaction.asset?.symbol}
              />
            ),
            align: 'right' as 'right',
          },
        ],
      },
      ...(transaction.networkFee
        ? [
            {
              cells: [
                { mainText: 'Network Fee' },
                {
                  mainText: (
                    <AmountListItem
                      amount={parseFloat(transaction.networkFee)}
                      subAmount={parseFloat(transaction.networkFeeInUsd)}
                      symbol={transaction.asset?.symbol}
                    />
                  ),
                  align: 'right' as 'right',
                },
              ],
            },
          ]
        : []),
      ...(transaction.sliverConvenienceFee
        ? [
            {
              cells: [
                { mainText: 'Service Fee' },
                {
                  mainText:
                    parseFloat(transaction.sliverConvenienceFee) === 0 ? (
                      <Chip
                        text={`Free`}
                        intent="accent"
                        setChipToNoMargin={true}
                      />
                    ) : (
                      <AmountListItem
                        amount={parseFloat(transaction.sliverConvenienceFee)}
                        subAmount={parseFloat(
                          transaction.sliverConvenienceFeeInUsd,
                        )}
                        symbol={transaction.asset?.symbol}
                      />
                    ),
                  align: 'right' as 'right',
                },
              ],
            },
          ]
        : []),
      {
        cells: [
          { mainText: 'Status' },
          {
            mainText: formatToSentenceCase(transaction.status),
            mainTextColor: getColorBasedOnState(transaction.status),
            subTextColor: getColorBasedOnState(transaction.status),
            align: 'right' as 'right',
          },
        ],
      },
    ];
    return (
      <Table
        columnWidths={columnWidths}
        rows={[...commonRows, ...earnRows]}
        isAscending={true}
      />
    );
  }
  if (transaction.type === 'trade') {
    const tradeRows: TableRowProps[] = [
      {
        cells: [
          { mainText: 'Bought' },
          {
            mainText: transaction.buyAsset ? (
              <TokenListItem
                token={transaction.buyAsset}
                align="right"
                showTicker={false}
                showNetwork
                showNetworkPrefix
                showSubNetworkNextToMainNetwork
              />
            ) : (
              'N/A'
            ),
            align: 'right' as 'right',
          },
        ],
      },
      {
        cells: [
          { mainText: 'Sold' },
          {
            mainText: transaction.sellAsset ? (
              <TokenListItem
                token={transaction.sellAsset}
                align="right"
                showTicker={false}
                showNetwork
                showNetworkPrefix
                showSubNetworkNextToMainNetwork
              />
            ) : (
              'N/A'
            ),
            align: 'right' as 'right',
          },
        ],
      },
      {
        cells: [
          { mainText: 'Amount' },
          {
            mainText: (
              <AmountListItem
                amount={parseFloat(transaction.amount)}
                subAmount={parseFloat(transaction.amountInUsd)}
                symbol={transaction.sellAsset?.symbol}
              />
            ),
            align: 'right' as 'right',
          },
        ],
      },
      ...(transaction.networkFee
        ? [
            {
              cells: [
                { mainText: 'Network Fee' },
                {
                  mainText: (
                    <AmountListItem
                      amount={parseFloat(transaction.networkFee)}
                      subAmount={parseFloat(transaction.networkFeeInUsd)}
                      symbol={transaction.sellAsset?.symbol}
                    />
                  ),
                  align: 'right' as 'right',
                },
              ],
            },
          ]
        : []),
      ...(transaction.sliverConvenienceFee
        ? [
            {
              cells: [
                { mainText: 'Service Fee' },
                {
                  mainText:
                    parseFloat(transaction.sliverConvenienceFee) === 0 ? (
                      <Chip
                        text={`Free`}
                        intent="accent"
                        setChipToNoMargin={true}
                      />
                    ) : (
                      <AmountListItem
                        amount={parseFloat(transaction.sliverConvenienceFee)}
                        subAmount={parseFloat(
                          transaction.sliverConvenienceFeeInUsd,
                        )}
                        symbol={transaction.sellAsset?.symbol}
                      />
                    ),
                  align: 'right' as 'right',
                },
              ],
            },
          ]
        : []),
      {
        cells: [
          { mainText: 'Status' },
          {
            mainText: formatToSentenceCase(transaction.status),
            mainTextColor: getColorBasedOnState(transaction.status),
            subTextColor: getColorBasedOnState(transaction.status),
            align: 'right' as 'right',
          },
        ],
      },
    ];
    return (
      <Table
        columnWidths={columnWidths}
        rows={[...commonRows, ...tradeRows]}
        isAscending={true}
      />
    );
  }
  const defaultRows: TableRowProps[] = [
    {
      cells: [
        { mainText: 'Asset' },
        {
          mainText: transaction.asset ? (
            <TokenListItem
              token={transaction.asset}
              align="right"
              showTicker={false}
              showNetwork
              showNetworkPrefix
              showSubNetworkNextToMainNetwork
            />
          ) : (
            'N/A'
          ),
          align: 'right' as 'right',
        },
      ],
    },
    {
      cells: [
        { mainText: 'Amount' },
        {
          mainText: (
            <AmountListItem
              amount={parseFloat(transaction.amount)}
              subAmount={parseFloat(transaction.amountInUsd)}
              symbol={transaction.asset?.symbol}
            />
          ),
          align: 'right' as 'right',
        },
      ],
    },
    ...(transaction.networkFee
      ? [
          {
            cells: [
              { mainText: 'Network Fee' },
              {
                mainText: (
                  <AmountListItem
                    amount={parseFloat(transaction.networkFee)}
                    subAmount={parseFloat(transaction.networkFeeInUsd)}
                    symbol={transaction.asset?.symbol}
                  />
                ),
                align: 'right' as 'right',
              },
            ],
          },
        ]
      : []),
    ...(transaction.sliverConvenienceFee
      ? [
          {
            cells: [
              { mainText: 'Service Fee' },
              {
                mainText:
                  parseFloat(transaction.sliverConvenienceFee) === 0 ? (
                    <Chip
                      text={`Free`}
                      intent="accent"
                      setChipToNoMargin={true}
                    />
                  ) : (
                    <AmountListItem
                      amount={parseFloat(transaction.sliverConvenienceFee)}
                      subAmount={parseFloat(
                        transaction.sliverConvenienceFeeInUsd,
                      )}
                      symbol={transaction.asset?.symbol}
                    />
                  ),
                align: 'right' as 'right',
              },
            ],
          },
        ]
      : []),
    {
      cells: [
        { mainText: 'Status' },
        {
          mainText: formatToSentenceCase(transaction.status),
          mainTextColor: getColorBasedOnState(transaction.status),
          subTextColor: getColorBasedOnState(transaction.status),
          align: 'right' as 'right',
        },
      ],
    },
  ];
  return (
    <Table
      columnWidths={columnWidths}
      rows={[...commonRows, ...defaultRows]}
      isAscending={true}
    />
  );
};
export default TransactionDetailList;
